"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { ArrowRight, BriefcaseBusiness, Hammer, HardHat } from "lucide-react";
import {
  MobileStickyAction,
  mobileStickyPrimaryClassName,
} from "@/components/funnel/mobile-sticky-action";
import { WizardStepHeader } from "@/components/funnel/wizard-step";
import { funnelCopy } from "@/lib/funnel/copy";
import { readBrowserAttribution } from "@/components/analytics/browser-attribution";

type RoleIntent = "RUNNING_THE_JOB" | "SOLO_TRADER" | "SOMEONE_ELSES_JOB";

const icons = {
  RUNNING_THE_JOB: HardHat,
  SOLO_TRADER: Hammer,
  SOMEONE_ELSES_JOB: BriefcaseBusiness,
};

export function RoleSelection({
  initialSessionToken,
  initialRoleIntent,
  preset = null,
}: {
  initialSessionToken: string | null;
  initialRoleIntent: RoleIntent | null;
  /** Job-pack preset key (slug:index) to carry through to the brief step. */
  preset?: string | null;
}) {
  const router = useRouter();
  const [roleIntent, setRoleIntent] = useState<RoleIntent | null>(initialRoleIntent);
  const [sessionToken, setSessionToken] = useState(initialSessionToken);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function continueToBrief(nextRoleIntent = roleIntent) {
    if (!nextRoleIntent) return;
    setRoleIntent(nextRoleIntent);
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(
        sessionToken ? `/api/draft-projects/${sessionToken}` : "/api/draft-projects",
        {
          method: sessionToken ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            roleIntent: nextRoleIntent,
            ...(sessionToken ? {} : readBrowserAttribution() ?? {}),
          }),
        },
      );
      if (!res.ok) throw new Error("Could not save your role");
      const data = (await res.json()) as { sessionToken: string };
      setSessionToken(data.sessionToken);
      router.push(
        preset ? `/start-project/brief?preset=${encodeURIComponent(preset)}` : "/start-project/brief",
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="rounded-lg border border-border bg-surface p-4 sm:p-7">
        <WizardStepHeader step={1} total={4} label="New project" title={funnelCopy.role.title}>
          <p>{funnelCopy.role.intro}</p>
        </WizardStepHeader>

        <div className="mt-6 grid gap-3">
          {funnelCopy.role.options.map((option) => {
            const Icon = icons[option.value];
            const selected = roleIntent === option.value;
            return (
              <button
                key={option.value}
                type="button"
                disabled={saving}
                onClick={() => setRoleIntent(option.value)}
                className={`flex min-h-[88px] items-start gap-4 rounded-lg border p-4 text-left transition-colors ${
                  selected
                    ? "border-accent bg-accent-light ring-2 ring-accent/20"
                    : "border-border bg-background hover:border-border-strong"
                }`}
                aria-pressed={selected}
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-surface text-navy">
                  <Icon className="h-5 w-5" aria-hidden />
                </span>
                <span>
                  <span className="block font-heading text-base font-bold text-navy">{option.title}</span>
                  <span className="mt-1 block text-sm leading-5 text-muted">{option.body}</span>
                </span>
              </button>
            );
          })}
        </div>

        {error ? <p className="mt-4 text-sm font-semibold text-danger">{error}</p> : null}

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            disabled={!roleIntent || saving}
            onClick={() => void continueToBrief()}
            className="hidden min-h-[44px] items-center justify-center gap-2 rounded-lg bg-accent px-5 py-3 text-sm font-bold text-white hover:bg-accent-dark disabled:cursor-not-allowed disabled:opacity-50 sm:inline-flex"
          >
            {saving ? "Saving..." : "Next"}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
      <MobileStickyAction>
        <button
          type="button"
          disabled={!roleIntent || saving}
          onClick={() => void continueToBrief()}
          className={mobileStickyPrimaryClassName}
        >
          {saving ? "Saving..." : "Next"}
          <ArrowRight className="h-5 w-5" aria-hidden />
        </button>
      </MobileStickyAction>
    </>
  );
}

